"use client";

import { FC } from "react";
import { twMerge } from "tailwind-merge";
import { SocialProviderType } from "../types/models";
import { providerTypeToIconLabel } from "./AddSocialProviderAccountButton";
import Avatar from "./Avatar";

// The canonical "who posts where" glyph: profile picture with the provider
// logo badged on the bottom-left corner. Every schedule surface (per-post
// card, calendar tile, trigger avatar stack, account/cadence picker rows,
// manage-accounts dialog) renders this one component so badge geometry
// never drifts between screens. Call sites only vary the avatar disc
// (`size`, plus separation / selection rings) — the badge is fixed.
//
// INVARIANT: the layout box is the disc, nothing more. The provider badge is
// absolutely positioned and every ring must be a `ring-*` (box-shadow), so
// both spill outside the box without widening it. That is what lets a face
// sit flush on a text column — a `border-*` would grow the disc instead
// (Avatar is `box-content`) and push the artwork off the column by the
// border's width.
/**
 * The disc scale, as named sizes rather than a class at each call site —
 * three jobs, three diameters:
 * "xs" (20px) is the compact face for calendar tiles and account selector
 * stacks. Its badge seat is -4px (not the shared -2px)
 * so the fixed 16px mark still hangs off the corner instead of covering
 * most of the disc.
 * "s" (24px) is the SUMMARY face — a facepile in a chip, a picker row, the
 * re-plan list — where a face is one of several read at a glance.
 * "m" (28px) is the POST face: the avatar in the schedule posts column's
 * rows, which carries a username beside it and a whole post indented under
 * it. It is that row's identity, not a glyph in a group of them, so it
 * leads at a size the summary faces do not.
 * The badge does NOT scale with the disc, and that is deliberate: it is a
 * label ON the face, not a part of it, so holding it at 16px keeps the
 * provider mark identical on every screen — ManageAccountsDialog's 32px
 * disc already wears this exact badge. A bigger disc buys the FACE more
 * room and changes nothing else, which is the whole of what "m" is for.
 */
export type AccountAvatarSize = "xs" | "s" | "m";

// Full class literals, never interpolated — Tailwind only sees what it can
// read in the source.
const DISC_CLASS: Record<AccountAvatarSize, string> = {
  xs: "size-5",
  s: "size-6",
  m: "size-7",
};

type AccountAvatarProps = {
  account: { profilePicture?: string | null; provider: SocialProviderType };
  /** Wrapper overrides: stacking margins, absolute positioning. */
  className?: string;
  /** The disc scale — see AccountAvatarSize. Defaults to the summary face. */
  size?: AccountAvatarSize;
  /**
   * Avatar disc overrides: rings, and a one-off diameter no `size` covers
   * (it wins the twMerge). Rings go through `ring-*`, never `border-*` —
   * see the invariant above.
   */
  avatarClassName?: string;
};

const AccountAvatar: FC<AccountAvatarProps> = ({
  account,
  className,
  size = "s",
  avatarClassName,
}) => (
  <div className={twMerge("relative shrink-0", className)}>
    <Avatar
      src={account.profilePicture}
      className={twMerge(DISC_CLASS[size], avatarClassName)}
    />
    {/* On xs the fixed 16px badge would sit deep into a 20px disc; push it
      further outside so it still reads as a corner label, not a cover.
      s/m keep the shared -2px seat. */}
    <div
      className={twMerge(
        "absolute w-4 h-4 p-0.5 rounded-md bg-white border border-slate-200 dark:bg-surface dark:border-surface-tertiary",
        size === "xs" ? "left-[-4px] bottom-[-4px]" : "left-[-2px] bottom-[-2px]",
      )}
    >
      {providerTypeToIconLabel[account.provider].icon}
    </div>
  </div>
);

export default AccountAvatar;
